import { createAsyncThunk, createAction, createSlice } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { DoctorRequestDto, DoctorResponseDto, Configuration, GlobalDoctorsApi } from "../api/client-axios";

import { ApiError } from "../model/apiError";
import { Problem } from "../model/problem";
import { initOtpState } from "./otpSlice";
import { apiConfiguration } from "./server";
import { applyRejection } from "./thunkError";

export interface DoctorState {
    doctor?: DoctorResponseDto,
    status: "idle" | "loading" | "error" | "success" | "auth-error" | "saved";
    error?: string | Problem;
    code?: number;
}


const initialDoctorState: DoctorState = {
    status: "idle",
    error: ""
}

export const fetchDoctor = createAsyncThunk<DoctorResponseDto, {organizationId: string, doctorId : string}, { rejectValue: ApiError }>('doctor/fetchDoctor', async (args, thunkApi) => {
    try {
        const api = new GlobalDoctorsApi(apiConfiguration());
        if (args.doctorId === "new") {
            thunkApi.dispatch(initOtpState());
            return {} as DoctorResponseDto;
        }
        const response = await api.apiV1AdministrationOrganizationOrganizationIdDoctorsIdGet(args.organizationId,args.doctorId)
        return response.data;
    } catch (error: AxiosError | any) {
        return thunkApi.rejectWithValue({ message: error?.response?.data, statusCode: error?.response?.status });
    }
});


export const saveDoctor = createAsyncThunk<DoctorResponseDto, {organizationId: string, doctor: DoctorRequestDto}, { rejectValue: ApiError }>('doctor/saveDoctor', async (args, thunkApi) => {
    try {
        const api = new GlobalDoctorsApi(apiConfiguration());
        const response = await api.apiV1AdministrationOrganizationOrganizationIdDoctorsPost( args.organizationId, args.doctor);
        return response.data;
    } catch (error: AxiosError | any) {
        return thunkApi.rejectWithValue({ message: error?.response?.data, statusCode: error?.response?.status });
    }
});


export const attachDoctor = createAsyncThunk<DoctorResponseDto, {organizationId: string, doctorId : string}, { rejectValue: ApiError }>('doctor/attachDoctor', async (args, thunkApi) => {
    try {
        const api = new GlobalDoctorsApi(apiConfiguration());
        const response = await api.apiV1AdministrationOrganizationOrganizationIdDoctorsIdAttachPost(args.doctorId, args.organizationId);
        return response.data;
    } catch (error: AxiosError | any) {
        return thunkApi.rejectWithValue({ message: error?.response?.data, statusCode: error?.response?.status });
    }
});

export const detachDoctor = createAsyncThunk<DoctorResponseDto, {organizationId: string, doctorId : string}, { rejectValue: ApiError }>('doctor/detachDoctor', async (args, thunkApi) => {
    try {
        const api = new GlobalDoctorsApi(apiConfiguration());
        const response = await api.apiV1AdministrationOrganizationOrganizationIdDoctorsIdDetachPost(args.doctorId, args.organizationId);
        return response.data;
    } catch (error: AxiosError | any) {
        return thunkApi.rejectWithValue({ message: error?.response?.data, statusCode: error?.response?.status });
    }
});


export const updateDoctorFields = createAction("doctor/updateDoctorFields",(doctor: DoctorRequestDto) => {return { payload: doctor }});
export const clearSaveDoctorError = createAction('doctor/clearError');
export const updateDoctorPhoneNumber = createAction('doctor/updatePhoneNumber', (phoneNumber: string) => { return { payload: phoneNumber }; });
export const initDoctorState = createAction('doctor/init');

const doctorSlice = createSlice({
    name: 'doctor',
    initialState: initialDoctorState,
    reducers: {},
    extraReducers: builder => {
        builder
            .addCase(fetchDoctor.pending, (state, _) => {
                state.status = "loading";
            })
            .addCase(fetchDoctor.fulfilled, (state, action) => {
                state.doctor = action.payload;
                state.status = "success";
            })
            .addCase(fetchDoctor.rejected, (state, action) => {
                applyRejection(state, action);
            })
            .addCase(saveDoctor.pending, (state, _) => {
                state.status = "loading";
            })
            .addCase(saveDoctor.fulfilled, (state, action) => {
                state.doctor = action.payload;
                state.status = "saved";
            })
            .addCase(saveDoctor.rejected, (state, action) => {
                applyRejection(state, action);
            })
            .addCase(clearSaveDoctorError, (state, action) => {
                state.status = "success";
                state.error = "";
            })
            .addCase(attachDoctor.pending, (state, _) => {
                state.status = "loading";
            })
            .addCase(attachDoctor.fulfilled, (state, action) => {
                state.doctor = action.payload;
                state.status = "saved";
            })
            .addCase(attachDoctor.rejected, (state, action) => {
                applyRejection(state, action);
            })
            .addCase(detachDoctor.pending, (state, _) => {
                state.status = "loading";
            })
            .addCase(detachDoctor.fulfilled, (state, action) => {
                state.doctor = action.payload;
                state.status = "saved";
            })
            .addCase(detachDoctor.rejected, (state, action) => {
                applyRejection(state, action);
            })
            .addCase(updateDoctorPhoneNumber, (state, action) => {
                state.doctor!.userAccount = { mobileNumber: action.payload, emailAddress: state.doctor?.userAccount?.emailAddress };
            })
            .addCase(initDoctorState,(state,action)=>{
                state.status = "idle";
                state.doctor = {};
                state.code = undefined;
                state.error = undefined;
            })
            .addCase(updateDoctorFields,(state,action)=>{
                state.doctor ={
                    ...state.doctor,
                    ...action.payload as DoctorResponseDto
                };
            });

    }
});

export default doctorSlice.reducer;
